/**
 * Platform detection - BULLETPROOF v3
 * 
 * Detects if the app runs inside Capacitor (iOS/Android).
 * NOT cached — checks every time because Capacitor may load AFTER the JS bundle.
 */

// Method 1: Build-time env var (set via REACT_APP_PLATFORM=mobile during yarn build)
const IS_MOBILE_BUILD = process.env.REACT_APP_PLATFORM === 'mobile';

/**
 * Returns true if running inside Capacitor (iOS/Android native app)
 * Checked on every call (not cached) because Capacitor bridge loads asynchronously
 */
export const isNativePlatform = () => {
  // 1. Build-time flag (most reliable if set)
  if (IS_MOBILE_BUILD) return true;

  try {
    // 2. Capacitor runtime API (available after Capacitor bridge loads)
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform()) return true;

    // 3. Capacitor object exists with platform info
    if (window.Capacitor && window.Capacitor.platform && window.Capacitor.platform !== 'web') return true;

    // 4. Our Capacitor hostname (set in capacitor.config.ts)
    if (window.location.hostname === 'app.emergentagent.com') return true;

    // 5. Capacitor protocol scheme
    if (window.location.protocol === 'capacitor:') return true;

    // 6. Check origin
    if (window.location.origin === 'https://app.emergentagent.com') return true;

    // 7. Check for Capacitor bridge / webkit messageHandlers (iOS WKWebView)
    if (window.CapacitorBridge || window.webkit?.messageHandlers?.bridge) return true;
    if (window.webkit?.messageHandlers?.cap) return true;

    // 8. iOS standalone mode (added to home screen or native)
    if (window.navigator?.standalone === true) return true;

    // 9. User agent
    const ua = navigator.userAgent || '';
    if (/CapacitorNative/i.test(ua)) return true;
  } catch (e) {
    // Safety: if any check throws, continue
  }

  return false;
};

/**
 * Check if user should have Club-level access.
 * Same logic on mobile and web — tier is checked against the database.
 * Apple IAP (RevenueCat) handles purchases on mobile.
 */
export const hasFullAccess = (subscriptionTier) => {
  return ['club', 'expert', 'pro', 'founder'].includes(subscriptionTier);
};

/**
 * Get the backend API base URL
 * - Mobile: Always use production (https://www.10minvestor.com)
 * - Web: Use REACT_APP_BACKEND_URL from environment
 */
export const getBackendUrl = () => {
  if (isNativePlatform()) {
    return 'https://www.10minvestor.com';
  }
  // Fallback safety: if hostname is our Capacitor hostname, use production
  try {
    if (window.location.hostname === 'app.emergentagent.com') {
      return 'https://www.10minvestor.com';
    }
  } catch (e) {}
  return process.env.REACT_APP_BACKEND_URL || '';
};

// Auth token management
const TOKEN_KEY = 'auth_token';

export const getStoredToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setStoredToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {}
};

export const clearStoredToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {}
};
